import {createContext, useLayoutEffect, useState, useSyncExternalStore, type ReactNode} from 'react'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type TimelineVerbosity = 'minimal' | 'balanced' | 'detailed'

export const TIMELINE_VERBOSITY_STORAGE_KEY = 'copilot-agent-sessions:timeline-verbosity'

const DEFAULT_TIMELINE_VERBOSITY: TimelineVerbosity = 'balanced'

export function isTimelineVerbosity(value: unknown): value is TimelineVerbosity {
  return value === 'minimal' || value === 'balanced' || value === 'detailed'
}

const storageOptions = {validator: isTimelineVerbosity}

export function useStoredTimelineVerbosity() {
  const [verbosity, setStoredVerbosity] = useLocalStorage<TimelineVerbosity>(
    TIMELINE_VERBOSITY_STORAGE_KEY,
    DEFAULT_TIMELINE_VERBOSITY,
    storageOptions,
  )
  const setVerbosity = (next: TimelineVerbosity) => setStoredVerbosity(next, {flush: true})
  return [verbosity, setVerbosity] as const
}

export const TimelineVerbosityContext = createContext<TimelineVerbosity>(DEFAULT_TIMELINE_VERBOSITY)

export function shouldShowTimelineVerbosityControl({
  showHeader,
  stickyHeaderEnabled,
  isAhpSession,
}: {
  showHeader: boolean
  stickyHeaderEnabled: boolean
  isAhpSession: boolean
}) {
  return showHeader && stickyHeaderEnabled && isAhpSession && isFeatureEnabled('one_copilot_grouped_tool_calls')
}

const subscribeToNothing = () => () => {}
const readDefaultTimelineVerbosity = () => DEFAULT_TIMELINE_VERBOSITY

let lastObservedTimelineVerbosity: TimelineVerbosity | undefined

export function resetTimelineVerbosityForTests() {
  lastObservedTimelineVerbosity = undefined
}

function readStoredTimelineVerbosity(): TimelineVerbosity {
  if (lastObservedTimelineVerbosity) return lastObservedTimelineVerbosity
  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(TIMELINE_VERBOSITY_STORAGE_KEY) ?? 'null')
    return isTimelineVerbosity(stored) ? stored : DEFAULT_TIMELINE_VERBOSITY
  } catch {
    return DEFAULT_TIMELINE_VERBOSITY
  }
}

export function TimelineVerbosityProvider({enabled, children}: {enabled: boolean; children: ReactNode}) {
  const [readRestoredVerbosity] = useState(() => {
    let restored: TimelineVerbosity | undefined
    return () => (restored ??= readStoredTimelineVerbosity())
  })
  const restoredVerbosity = useSyncExternalStore(
    subscribeToNothing,
    enabled ? readRestoredVerbosity : readDefaultTimelineVerbosity,
    readDefaultTimelineVerbosity,
  )
  const [storedVerbosity, setStoredVerbosity] = useState<TimelineVerbosity>(restoredVerbosity)
  return (
    <TimelineVerbosityContext value={enabled ? storedVerbosity : DEFAULT_TIMELINE_VERBOSITY}>
      {enabled && <StoredTimelineVerbosityObserver onChange={setStoredVerbosity} />}
      {children}
    </TimelineVerbosityContext>
  )
}

function StoredTimelineVerbosityObserver({onChange}: {onChange: (verbosity: TimelineVerbosity) => void}) {
  const [verbosity] = useStoredTimelineVerbosity()
  useLayoutEffect(() => {
    lastObservedTimelineVerbosity = verbosity
    onChange(verbosity)
  }, [onChange, verbosity])
  return null
}
